import React from 'react';
import { useTranslation } from 'react-i18next';
import { dataFormatEnum } from '../../models-enums';

export interface IUnit {
    unitNumber: number;
    label?: string; // Optional label from API (e.g., "Unit 1" or "Dryer 1: Operating Days")
    hours: number;
    unit?: string; // Optional unit type from API (e.g., "Hours" or "Days")
    mode: string | null; // null when operating mode sensor is missing
    type?: 'element' | 'dryer'; // Explicit type from API for color logic
}

interface UnitsCardProps {
    units: IUnit[];
}

// SmartLink colors matching old dashboard (AnalyticsExpandedCard.tsx)
const SMARTLINK_COLORS = {
    red: '#d20404',
    blue: '#0070c0',
    yellow: '#ffc000',
    defaultBorder: '#e0e0e0' // Neutral border when mode is null
};

/**
 * Get color for unit based on OLD dashboard logic from AnalyticsExpandedCard.tsx
 *
 * MMG-3764: null/missing mode - MODE UI element hidden entirely (not shown)
 *
 * Elements:
 *   - OFF (explicit sensor value) = RED
 *   - AUTOMATIC = BLUE
 *   - other = YELLOW (default)
 *
 * Dryers:
 *   - OFF (explicit sensor value) = RED
 *   - MANUAL = YELLOW
 *   - other = BLUE (default)
 */
const getUnitColor = (mode: string | null, isDryer: boolean): string => {
    // MMG-3764: When mode is null, return neutral border color
    if (!mode || mode.trim() === '') {
        return SMARTLINK_COLORS.defaultBorder;
    }

    const modeUpper = mode.toUpperCase();

    if (isDryer) {
        // Dryer logic: default BLUE, OFF=RED, Manual=YELLOW
        if (modeUpper === 'OFF') {
            return SMARTLINK_COLORS.red;
        } else if (modeUpper === 'MANUAL') {
            return SMARTLINK_COLORS.yellow;
        }
        return SMARTLINK_COLORS.blue; // default for dryers
    } else {
        // Element logic: default YELLOW, OFF=RED, Automatic=BLUE
        if (modeUpper === 'OFF') {
            return SMARTLINK_COLORS.red;
        } else if (modeUpper === 'AUTOMATIC') {
            return SMARTLINK_COLORS.blue;
        }
        return SMARTLINK_COLORS.yellow; // default for elements
    }
};

const UnitsCard: React.FC<UnitsCardProps> = ({ units }) => {
    const { t } = useTranslation();
    if (!units || units.length === 0) {
        return null;
    }

    return (
        <div className="units-card">
            <div className="units-card-header">
                <h3>Units</h3>
            </div>
            <div className="units-card-grid">
                {units.map((unit, index) => {
                    // Use explicit type from API, fallback to unitNumber inference for backward compatibility
                    const isDryer =
                        unit.type === 'dryer' || unit.unitNumber > 100;
                    const color = getUnitColor(unit.mode, isDryer);

                    return (
                        <div
                            key={index}
                            className="unit-item"
                            style={{ borderColor: color }}
                        >
                            <div className="unit-item-number">
                                {unit.label
                                    ? unit.label.toUpperCase()
                                    : `UNIT ${unit.unitNumber}`}
                            </div>
                            <div className="unit-item-hours">
                                {t(
                                    `common:format.${
                                        dataFormatEnum[
                                            dataFormatEnum.numberTruncate
                                        ]
                                    }`,
                                    {
                                        numberString: unit.hours
                                    }
                                )}
                                <span className="unit-item-hours-label">
                                    {' '}
                                    {unit.unit || 'Hours'}
                                </span>
                            </div>
                            {unit.mode && (
                                <div
                                    className="unit-item-mode"
                                    style={{
                                        backgroundColor: color,
                                        color: '#fff'
                                    }}
                                >
                                    MODE: {unit.mode}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UnitsCard;
